import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '@/lib/useReducedMotion';

interface CountUpProps {
  /** The final string, e.g. "20", "925 MB", "17". Digits animate, units do not. */
  value: string;
  duration?: number;
}

/**
 * Counts a figure up when it scrolls into view.
 *
 * The final value is rendered on the server and restored the moment the
 * animation ends, so the number in the DOM is never wrong for a crawler, for a
 * reduced-motion visitor, or after the effect completes.
 */
export function CountUp({ value, duration = 1400 }: CountUpProps) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const el = ref.current;
    // No observer (older browsers, jsdom) or motion is unwanted: show the real
    // figure. The animation is decoration; the number is the content.
    if (!el || reduced || !('IntersectionObserver' in window)) {
      setDisplay(value);
      return;
    }

    const match = value.match(/^([\d,.]+)(.*)$/);
    if (!match) return;

    const target = parseFloat(match[1].replace(/,/g, ''));
    const suffix = match[2];
    if (!Number.isFinite(target)) return;

    let raf = 0;
    let started = false;

    const run = (startTime: number) => {
      const step = (now: number) => {
        const t = Math.min(1, (now - startTime) / duration);
        // easeOutExpo: fast commitment, long settle.
        const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
        const current = target * eased;
        const formatted =
          target >= 100 ? Math.round(current).toLocaleString('en-GB') : current.toFixed(0);
        setDisplay(formatted + suffix);
        if (t < 1) raf = requestAnimationFrame(step);
        else setDisplay(value);
      };
      raf = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || started) continue;
          started = true;
          setDisplay(target >= 100 ? '0' : '0');
          run(performance.now());
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [value, duration, reduced]);

  return (
    <span ref={ref} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {display}
    </span>
  );
}
